import { Controller } from "@hotwired/stimulus"

// Tracks selection state for invoice rows and toggles a sticky bar that
// submits the selected ids to /payment_batches/new.
//
// The selection is persisted in sessionStorage so it survives the full-page
// navigations that Pagy pagination, the filter pills and search trigger —
// letting the user gather invoices across several pages into one batch.
//
// A payment batch pays every row from a single legal entity's account, so the
// selection is locked to one entity: ticking an invoice from a different
// entity is refused with a hint until the current selection is cleared.
export default class extends Controller {
  static targets = ["checkbox", "bar", "count", "form", "hiddenContainer", "selectAll", "entityName", "notice"]
  static values = { storageKey: { type: String, default: "payment-batch-picker" } }

  connect() {
    this.selection = this.#load()
    this.#restoreCheckboxes()
    this.#render()
  }

  toggle(event) {
    const cb = event.target
    if (cb.checked && !this.#acceptEntity(cb)) {
      cb.checked = false
      return
    }
    this.#applyCheckbox(cb)
    this.#persist()
    this.#render()
  }

  toggleAll(event) {
    const checked = event.target.checked
    let skipped = 0

    this.checkboxTargets.forEach((cb) => {
      if (checked) {
        if (!this.#acceptEntity(cb)) {
          cb.checked = false
          skipped += 1
          return
        }
        cb.checked = true
      } else {
        cb.checked = false
      }
      this.#applyCheckbox(cb)
    })

    this.#persist()
    this.#render()
    if (skipped > 0) this.#showNotice(this.#otherEntityMessage())
  }

  clear() {
    this.selection = {}
    this.checkboxTargets.forEach((cb) => { cb.checked = false })
    this.#persist()
    this.#render()
  }

  // Called on form submit — the hidden inputs are already serialized into the
  // request, so we just drop the stored selection to avoid it resurfacing
  // (e.g. on a Back navigation) after the batch has been created.
  clearStorage() {
    this.selection = {}
    this.#persist()
  }

  // ── internals ──────────────────────────────────────────────────────────

  // Records or removes a single checkbox's invoice in the selection map.
  #applyCheckbox(cb) {
    if (cb.checked) {
      this.selection[cb.value] = {
        entityId: cb.dataset.legalEntityId,
        entityName: cb.dataset.legalEntityName
      }
    } else {
      delete this.selection[cb.value]
      this.#clearNotice()
    }
  }

  // True when the checkbox belongs to the same legal entity as the current
  // selection (or the selection is empty). Otherwise flashes a hint.
  #acceptEntity(cb) {
    const current = this.#selectedEntityId()
    if (current === null || current === cb.dataset.legalEntityId) return true
    this.#showNotice(this.#otherEntityMessage())
    return false
  }

  #selectedEntityId() {
    const first = Object.values(this.selection)[0]
    return first ? first.entityId : null
  }

  #selectedEntityName() {
    const first = Object.values(this.selection)[0]
    return first ? first.entityName : null
  }

  // Restores checkbox state on this page from the persisted selection.
  #restoreCheckboxes() {
    this.checkboxTargets.forEach((cb) => {
      cb.checked = Object.prototype.hasOwnProperty.call(this.selection, cb.value)
    })
  }

  #render() {
    const ids = Object.keys(this.selection)
    const count = ids.length

    if (this.hasCountTarget) this.countTarget.textContent = count
    if (this.hasEntityNameTarget) {
      this.entityNameTarget.textContent = this.#selectedEntityName() || ""
    }

    if (this.hasBarTarget) {
      // Only slide/fade the wrapper; it stays pointer-events-none always so its
      // transparent gutter never blocks the pagination. The inner bar carries
      // pointer-events-auto, so its buttons remain clickable when shown.
      const hidden = count === 0
      this.barTarget.classList.toggle("translate-y-full", hidden)
      this.barTarget.classList.toggle("opacity-0", hidden)
    }

    if (this.hasSelectAllTarget) {
      const onPage = this.checkboxTargets.length
      const checkedOnPage = this.checkboxTargets.filter((cb) => cb.checked).length
      this.selectAllTarget.checked = checkedOnPage > 0 && checkedOnPage === onPage
      this.selectAllTarget.indeterminate = checkedOnPage > 0 && checkedOnPage < onPage
    }

    if (this.hasHiddenContainerTarget) {
      this.hiddenContainerTarget.innerHTML = ""
      ids.forEach((id) => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "invoice_ids[]"
        input.value = id
        this.hiddenContainerTarget.appendChild(input)
      })
    }
  }

  #otherEntityMessage() {
    const name = this.#selectedEntityName()
    return name
      ? `Paczka obejmuje jeden podmiot (${name}). Wyczyść zaznaczenie, aby wybrać faktury innego podmiotu.`
      : "Paczka może obejmować faktury tylko jednego podmiotu."
  }

  #showNotice(message) {
    if (!this.hasNoticeTarget) return
    this.noticeTarget.textContent = message
    this.noticeTarget.classList.remove("hidden")
  }

  #clearNotice() {
    if (this.hasNoticeTarget) this.noticeTarget.classList.add("hidden")
  }

  #load() {
    try {
      const raw = window.sessionStorage.getItem(this.storageKeyValue)
      return raw ? JSON.parse(raw) : {}
    } catch (_e) {
      return {}
    }
  }

  #persist() {
    try {
      window.sessionStorage.setItem(this.storageKeyValue, JSON.stringify(this.selection))
    } catch (_e) {
      // sessionStorage unavailable (private mode / quota) — selection still
      // works within the current page, just not across navigations.
    }
  }
}
